/**
 * 标准配合选择候选转换模块。
 *
 * 文件职责：
 * 1. 将 `SelectionUpdated` 稳定快照转换为 Mate 可消费候选；
 * 2. 保留装配拓扑稳定引用与世界空间几何描述符；
 * 3. 禁止从 UI 层或 Vue 响应式对象反推 BRep 语义。
 */

import type {
  AssemblyReferenceSelectionSnapshotEntry,
  AssemblyTopologySelectionSnapshotEntry,
  SelectionUpdatedEventPayload,
} from '../../manager/EntitySelectionManager';
import {
  ASSEMBLY_MATE_SCHEMA_VERSION,
  cloneComponentTransformSnapshot,
  MateEntityKind,
  type MateEntityRef,
} from '../definition';
import {
  cloneAssemblyReferenceDependencySnapshot,
  resolveSingleAssemblyReferenceDependencyDriverId,
  type AssemblyReferenceDependencySnapshot,
} from '../definition/AssemblyReferenceDependency';

/**
 * 配合选择候选。
 */
export interface AssemblyMateSelectionCandidate {
  /** 配合实体引用。 */
  readonly entityRef: MateEntityRef;
  /** 候选显示名称。 */
  readonly label: string;
}

/**
 * 判断有限数字。
 *
 * @param value 待判断值。
 * @returns 是否为有限数字。
 */
function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * 判断三维点元组。
 *
 * @param value 待判断值。
 * @returns 是否为三维点元组。
 */
function isPointTuple(value: unknown): value is TopologyPoint3D {
  return Array.isArray(value)
    && value.length === 3
    && value.every((item) => isFiniteNumber(item));
}

/**
 * 从 descriptor 解析配合实体语义。
 *
 * @param descriptor BRep 几何描述符。
 * @returns 配合实体语义。
 */
export function resolveMateEntityKindFromBRepDescriptor(
  descriptor: BRepGeometryDescriptor,
): MateEntityKind {
  if (descriptor.entityKind === 'vertex') {
    return MateEntityKind.Point;
  }

  if (descriptor.entityKind === 'origin') {
    return MateEntityKind.Origin;
  }

  if (descriptor.entityKind === 'coordinateSystem') {
    return MateEntityKind.CoordinateSystem;
  }

  if (descriptor.entityKind === 'referenceAxis') {
    return MateEntityKind.ReferenceAxis;
  }

  if (descriptor.entityKind === 'referencePlane') {
    return MateEntityKind.ReferencePlane;
  }

  if (descriptor.entityKind === 'edge') {
    if (descriptor.curveKind === 'line') {
      return MateEntityKind.Line;
    }
    if (descriptor.curveKind === 'circle') {
      return MateEntityKind.CircularEdge;
    }
    return MateEntityKind.Curve;
  }

  if (descriptor.entityKind === 'face') {
    if (descriptor.surfaceKind === 'plane') {
      return MateEntityKind.Plane;
    }
    if (descriptor.surfaceKind === 'cylinder') {
      return MateEntityKind.Cylinder;
    }
    if (descriptor.surfaceKind === 'cone') {
      return MateEntityKind.Cone;
    }
    if (descriptor.surfaceKind === 'sphere') {
      return MateEntityKind.Sphere;
    }
    return MateEntityKind.Surface;
  }

  return MateEntityKind.Unsupported;
}

/**
 * 构建配合实体稳定 ID。
 *
 * @param ref 装配拓扑引用。
 * @param descriptor BRep 几何描述符。
 * @returns 稳定 ID。
 */
function buildMateEntityStableRef(
  ref: AssemblyTopologySelectionRef,
  descriptor: BRepGeometryDescriptor,
): string {
  const topologyRef = ref.persistentRef || descriptor.persistentRef || String(descriptor.topologyIndex);
  return [
    ref.assemblyNodeId,
    ref.partUuid ?? 'local',
    ref.bodyBindingId,
    ref.entityKind,
    topologyRef,
  ].join(':');
}

/**
 * 构建基准引用 Mate 稳定 ID。
 *
 * @param ref 基准选择稳定引用。
 * @returns MateEntityRef 可保存的稳定 ID。
 */
function buildMateReferenceStableRef(ref: AssemblyReferenceSelectionRef): string {
  return ref.stableRef;
}

/**
 * 创建候选显示名称。
 *
 * @param entityKind 实体语义。
 * @param index 候选序号。
 * @returns 展示名称。
 */
function createCandidateLabel(entityKind: MateEntityKind, index: number): string {
  const kindLabelMap: Record<MateEntityKind, string> = {
    [MateEntityKind.Plane]: '面',
    [MateEntityKind.Cylinder]: '面',
    [MateEntityKind.Cone]: '面',
    [MateEntityKind.Sphere]: '面',
    [MateEntityKind.Surface]: '面',
    [MateEntityKind.Line]: '线/轴',
    [MateEntityKind.CircularEdge]: '圆边/圆弧',
    [MateEntityKind.Curve]: '曲线',
    [MateEntityKind.Point]: '点',
    [MateEntityKind.Origin]: '原点',
    [MateEntityKind.CoordinateSystem]: '坐标系',
    [MateEntityKind.ReferenceAxis]: '基准轴',
    [MateEntityKind.ReferencePlane]: '基准面',
    [MateEntityKind.Unsupported]: '不支持实体',
  };
  return `${kindLabelMap[entityKind]}${index + 1}`;
}

/**
 * 重建候选在配合列表中的显示序号。
 *
 * @param candidate 原始配合候选。
 * @param index 当前命令槽位序号。
 * @returns 使用当前槽位序号命名后的候选。
 */
function relabelAssemblyMateSelectionCandidate(
  candidate: AssemblyMateSelectionCandidate,
  index: number,
): AssemblyMateSelectionCandidate {
  const label = createCandidateLabel(candidate.entityRef.entityKind, index);
  return {
    ...candidate,
    label,
    entityRef: {
      ...candidate.entityRef,
      label,
    },
  };
}

/**
 * 从已持久化 Mate 实体重建 PropertyManager 候选。
 *
 * 设计说明：
 * 1. SOLIDWORKS 从设计树编辑既有 Mate 时，会把原来的两个配合实体恢复到 `Entities to Mate`；
 * 2. 该恢复必须使用 Mate Definition 中保存的稳定实体引用，不能重新读取 web-client 或 Object3D；
 * 3. 这里仅复制可序列化数据并重建显示序号，后续兼容性、预览和提交仍由 cad-core 控制器统一处理。
 *
 * @param entities 已持久化的 Mate 实体引用。
 * @returns 可放入当前 Mate PropertyManager 的候选集合。
 */
export function createAssemblyMateSelectionCandidatesFromEntityRefs(
  entities: readonly MateEntityRef[],
): AssemblyMateSelectionCandidate[] {
  return entities.map((entity, index) => {
    const label = createCandidateLabel(entity.entityKind, index);
    return {
      label,
      entityRef: {
        ...entity,
        ...(entity.topologyRef ? { topologyRef: cloneAssemblyTopologySelectionRef(entity.topologyRef) } : {}),
        ...(entity.referenceRef ? { referenceRef: cloneAssemblyReferenceSelectionRef(entity.referenceRef) } : {}),
        ...(entity.componentTransformAtSelection
          ? { componentTransformAtSelection: cloneComponentTransformSnapshot(entity.componentTransformAtSelection) }
          : {}),
        ...(entity.componentWorldTransformAtSelection
          ? {
              componentWorldTransformAtSelection: cloneComponentTransformSnapshot(
                entity.componentWorldTransformAtSelection,
              ),
            }
          : {}),
        geometry: cloneBRepGeometryDescriptor(entity.geometry),
        label,
      },
    };
  });
}

/**
 * 判断两个候选是否指向同一个稳定拓扑实体。
 *
 * @param left 左侧候选。
 * @param right 右侧候选。
 * @returns 两者是否为同一候选。
 */
function isSameAssemblyMateSelectionCandidate(
  left: AssemblyMateSelectionCandidate,
  right: AssemblyMateSelectionCandidate,
): boolean {
  return left.entityRef.stableRef === right.entityRef.stableRef;
}

/**
 * 复制装配拓扑引用。
 *
 * @param ref 源引用。
 * @returns 引用副本。
 */
function cloneAssemblyTopologySelectionRef(
  ref: AssemblyTopologySelectionRef,
): AssemblyTopologySelectionRef {
  return {
    ...ref,
    adjacentFaceRefs: ref.adjacentFaceRefs ? [...ref.adjacentFaceRefs] : undefined,
  };
}

/**
 * 复制装配体基准引用。
 *
 * @param ref 源基准引用。
 * @returns 基准引用副本。
 */
function cloneAssemblyReferenceSelectionRef(
  ref: AssemblyReferenceSelectionRef,
): AssemblyReferenceSelectionRef {
  return {
    ...ref,
  };
}

/**
 * 克隆 BRep 描述符。
 *
 * @param descriptor 源描述符。
 * @returns 描述符副本。
 */
export function cloneBRepGeometryDescriptor(
  descriptor: BRepGeometryDescriptor,
): BRepGeometryDescriptor {
  const cloned = {
    ...descriptor,
  } as BRepGeometryDescriptor & Record<string, unknown>;

  [
    'point',
    'normal',
    'start',
    'end',
    'direction',
    'center',
    'axisOrigin',
    'axisDirection',
    'apex',
    'xAxis',
    'yAxis',
    'zAxis',
    'origin',
  ].forEach((key) => {
    const value = cloned[key];
    if (isPointTuple(value)) {
      cloned[key] = [value[0], value[1], value[2]];
    }
  });

  if (Array.isArray(cloned.sampledPoints)) {
    cloned.sampledPoints = cloned.sampledPoints.map((point) => [point[0], point[1], point[2]]);
  }

  return cloned;
}

/**
 * 从单条稳定选择快照创建配合候选。
 *
 * @param topologyRef 装配拓扑稳定引用。
 * @param geometry BRep 几何描述符。
 * @param index 候选显示序号。
 * @returns 可用于配合 PropertyManager 的候选。
 */
function createAssemblyMateSelectionCandidate(
  topologyRef: AssemblyTopologySelectionRef,
  geometry: BRepGeometryDescriptor,
  index: number,
): AssemblyMateSelectionCandidate {
  const entityKind = resolveMateEntityKindFromBRepDescriptor(geometry);
  const label = createCandidateLabel(entityKind, index);
  const clonedTopologyRef = cloneAssemblyTopologySelectionRef(topologyRef);
  const clonedGeometry = cloneBRepGeometryDescriptor(geometry);

  return {
    label,
    entityRef: {
      schemaVersion: ASSEMBLY_MATE_SCHEMA_VERSION,
      stableRef: buildMateEntityStableRef(clonedTopologyRef, clonedGeometry),
      assemblyNodeId: clonedTopologyRef.assemblyNodeId,
      componentInstanceId: clonedTopologyRef.assemblyNodeId,
      partUuid: clonedTopologyRef.partUuid,
      entityKind,
      topologyRef: clonedTopologyRef,
      geometry: clonedGeometry,
      label,
    },
  };
}

/**
 * 从基准引用快照创建配合候选。
 *
 * @param referenceRef 装配体基准引用。
 * @param geometry 基准引用几何描述符。
 * @param index 候选显示序号。
 * @returns 可用于配合 PropertyManager 的候选。
 */
function createAssemblyMateReferenceSelectionCandidate(
  referenceRef: AssemblyReferenceSelectionRef,
  geometry: BRepGeometryDescriptor,
  index: number,
  referenceDependency?: AssemblyReferenceDependencySnapshot | null,
): AssemblyMateSelectionCandidate {
  const entityKind = resolveMateEntityKindFromBRepDescriptor(geometry);
  const label = createCandidateLabel(entityKind, index);
  const clonedReferenceRef = cloneAssemblyReferenceSelectionRef(referenceRef);
  const clonedGeometry = cloneBRepGeometryDescriptor(geometry);
  const clonedReferenceDependency = cloneAssemblyReferenceDependencySnapshot(referenceDependency);
  const driverComponentInstanceId = resolveSingleAssemblyReferenceDependencyDriverId(clonedReferenceDependency);
  const componentInstanceId = driverComponentInstanceId ?? clonedReferenceRef.stableRef;

  return {
    label,
    entityRef: {
      schemaVersion: ASSEMBLY_MATE_SCHEMA_VERSION,
      stableRef: buildMateReferenceStableRef(clonedReferenceRef),
      assemblyNodeId: componentInstanceId,
      componentInstanceId,
      entityKind,
      referenceRef: clonedReferenceRef,
      ...(clonedReferenceDependency ? { referenceDependency: clonedReferenceDependency } : {}),
      geometry: clonedGeometry,
      label,
    },
  };
}

/**
 * 按 SelectionUpdated 的对象顺序合并拓扑与基准引用快照。
 *
 * @param selectedObjectIds 当前选择对象 ID 顺序。
 * @param topologyEntries 拓扑选择快照。
 * @param referenceEntries 基准引用选择快照。
 * @returns 按真实选择顺序排序后的快照。
 */
function mergeSelectionSnapshotEntriesBySelectionOrder(
  selectedObjectIds: readonly string[] | undefined,
  topologyEntries: readonly AssemblyTopologySelectionSnapshotEntry[],
  referenceEntries: readonly AssemblyReferenceSelectionSnapshotEntry[],
): Array<AssemblyTopologySelectionSnapshotEntry | AssemblyReferenceSelectionSnapshotEntry> {
  const entries = [...topologyEntries, ...referenceEntries];
  if (!selectedObjectIds || selectedObjectIds.length === 0) {
    return entries;
  }

  const orderByObjectId = new Map(selectedObjectIds.map((objectId, index) => [objectId, index]));
  return entries.sort((left, right) => {
    const leftOrder = orderByObjectId.get(left.objectId) ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = orderByObjectId.get(right.objectId) ?? Number.MAX_SAFE_INTEGER;
    return leftOrder - rightOrder;
  });
}

/**
 * 判断选择快照是否为基准引用。
 *
 * @param entry 选择快照。
 * @returns 是否为基准引用快照。
 */
function isAssemblyReferenceSelectionSnapshotEntry(
  entry: AssemblyTopologySelectionSnapshotEntry | AssemblyReferenceSelectionSnapshotEntry,
): entry is AssemblyReferenceSelectionSnapshotEntry {
  return 'referenceRef' in entry;
}

/**
 * 从稳定选择快照创建配合候选。
 *
 * 设计说明：
 * 1. `assemblyTopologySelectionEntries` 是同一次命中的拓扑引用和 BRep 描述符成对快照，优先使用它避免过滤后数组下标错位；
 * 2. 旧字段仍保留为兼容入口，只在旧调用方尚未提供成对快照时兜底；
 * 3. 该函数只复制可序列化数据，不读取 Three.js 运行时对象，保证 UI 层无法绕过 cad-core 规则。
 *
 * @param payload 选择完成后的稳定快照。
 * @returns 可用于配合 PropertyManager 的候选集合。
 */
export function createAssemblyMateSelectionCandidatesFromSnapshot(
  payload: Pick<SelectionUpdatedEventPayload, 'assemblyTopologyRefs' | 'brepGeometryDescriptors'>
    & Partial<Pick<
      SelectionUpdatedEventPayload,
      'selectedObjectIds' | 'assemblyTopologySelectionEntries' | 'assemblyReferenceSelectionEntries'
    >>,
): AssemblyMateSelectionCandidate[] {
  const pairedEntries = payload.assemblyTopologySelectionEntries ?? [];
  const referenceEntries = payload.assemblyReferenceSelectionEntries ?? [];
  if (pairedEntries.length > 0 || referenceEntries.length > 0) {
    return mergeSelectionSnapshotEntriesBySelectionOrder(
      payload.selectedObjectIds,
      pairedEntries,
      referenceEntries,
    ).map((entry, index) => (
      isAssemblyReferenceSelectionSnapshotEntry(entry)
        ? createAssemblyMateReferenceSelectionCandidate(
            entry.referenceRef,
            entry.brepGeometryDescriptor,
            index,
            entry.referenceDependency,
          )
        : createAssemblyMateSelectionCandidate(entry.assemblyTopologyRef, entry.brepGeometryDescriptor, index)
    ));
  }

  const count = Math.min(payload.assemblyTopologyRefs.length, payload.brepGeometryDescriptors.length);
  const candidates: AssemblyMateSelectionCandidate[] = [];

  for (let index = 0; index < count; index += 1) {
    const topologyRef = payload.assemblyTopologyRefs[index];
    const geometry = payload.brepGeometryDescriptors[index];
    if (!topologyRef || !geometry) {
      continue;
    }
    candidates.push(createAssemblyMateSelectionCandidate(topologyRef, geometry, index));
  }

  return candidates;
}

/**
 * 将新的 SelectionUpdated 候选吸收到当前配合选择槽位。
 *
 * 设计说明：
 * 1. 场景选择事件通常只携带“本次点击”的稳定拓扑引用，不能直接覆盖 Mate PropertyManager 中已吸收的槽位；
 * 2. SOLIDWORKS 的配合选择框在激活后按点击顺序吸收实体，达到上限后忽略额外输入；
 * 3. 该纯函数只处理稳定引用与序号，不读取 Three.js / Vue 运行时对象，保证 UI 层无需参与业务判断。
 *
 * @param current 当前命令已吸收的候选。
 * @param incoming 本次 SelectionUpdated 转换出的候选。
 * @param maxCount 最多允许吸收的候选数量。
 * @returns 合并后的候选列表。
 */
export function absorbAssemblyMateSelectionCandidates(
  current: readonly AssemblyMateSelectionCandidate[],
  incoming: readonly AssemblyMateSelectionCandidate[],
  maxCount = 2,
): AssemblyMateSelectionCandidate[] {
  const result = current.slice(0, maxCount);

  for (const candidate of incoming) {
    if (result.length >= maxCount) {
      break;
    }
    if (result.some((item) => isSameAssemblyMateSelectionCandidate(item, candidate))) {
      continue;
    }
    result.push(candidate);
  }

  return result.map((candidate, index) => relabelAssemblyMateSelectionCandidate(candidate, index));
}

/**
 * 将新的 SelectionUpdated 候选替换到指定配合选择槽位。
 *
 * 设计说明：
 * 1. SOLIDWORKS 允许用户在 Mate Selections 列表中点中某一项后重新从图形区选择实体；
 * 2. 替换逻辑必须在 cad-core 内完成，web-client 不能自行拼接 MateEntityRef；
 * 3. 若替换实体已存在于其它槽位，则保持当前选择不变，避免两个槽位引用同一稳定实体。
 *
 * @param current 当前命令已吸收的候选。
 * @param incoming 本次 SelectionUpdated 转换出的候选。
 * @param index 要替换的槽位索引。
 * @param maxCount 最多允许保留的候选数量。
 * @returns 替换并重排后的候选列表。
 */
export function replaceAssemblyMateSelectionCandidateAt(
  current: readonly AssemblyMateSelectionCandidate[],
  incoming: readonly AssemblyMateSelectionCandidate[],
  index: number,
  maxCount = 2,
): AssemblyMateSelectionCandidate[] {
  if (!Number.isInteger(index) || index < 0 || index >= maxCount) {
    return absorbAssemblyMateSelectionCandidates(current, incoming, maxCount);
  }

  const result = current.slice(0, maxCount);
  const replacement = incoming.find((candidate) => !result.some((item, candidateIndex) => (
    candidateIndex !== index && isSameAssemblyMateSelectionCandidate(item, candidate)
  )));

  if (!replacement) {
    return result.map((candidate, candidateIndex) => relabelAssemblyMateSelectionCandidate(candidate, candidateIndex));
  }

  if (index < result.length) {
    result[index] = replacement;
  } else {
    result.push(replacement);
  }

  return result.map((candidate, candidateIndex) => relabelAssemblyMateSelectionCandidate(candidate, candidateIndex));
}

/**
 * 删除指定槽位中的配合选择候选。
 *
 * 设计说明：
 * 1. SOLIDWORKS 的 Mate Selections 允许删除单个已选实体后继续从图形区补选；
 * 2. 删除后槽位序号必须重新计算，否则 UI 显示、默认 Mate 类型和持久化标签会出现错位；
 * 3. 该函数只处理稳定 MateEntityRef，不依赖 UI、Three.js 或运行时 Object3D。
 *
 * @param current 当前命令已吸收的候选。
 * @param index 要删除的槽位索引。
 * @returns 删除并重排后的候选列表。
 */
export function removeAssemblyMateSelectionCandidateAt(
  current: readonly AssemblyMateSelectionCandidate[],
  index: number,
): AssemblyMateSelectionCandidate[] {
  if (!Number.isInteger(index) || index < 0 || index >= current.length) {
    return current.map((candidate, candidateIndex) => relabelAssemblyMateSelectionCandidate(candidate, candidateIndex));
  }

  return current
    .filter((_, candidateIndex) => candidateIndex !== index)
    .map((candidate, candidateIndex) => relabelAssemblyMateSelectionCandidate(candidate, candidateIndex));
}

/**
 * 批量删除指定槽位中的配合选择候选。
 *
 * 设计说明：
 * 1. SOLIDWORKS 的 Mate Selections 列表支持选中多个条目后删除；
 * 2. 删除集合可能来自 UI 多选态，顺序、重复项和越界项都不能影响最终结果；
 * 3. 这里仍只处理稳定 MateEntityRef，不让 web-client 自行维护候选数组。
 *
 * @param current 当前命令已吸收的候选。
 * @param indices 要删除的槽位索引集合。
 * @returns 删除并重排后的候选列表。
 */
export function removeAssemblyMateSelectionCandidatesAt(
  current: readonly AssemblyMateSelectionCandidate[],
  indices: readonly number[],
): AssemblyMateSelectionCandidate[] {
  const indexSet = new Set(indices.filter((index) => (
    Number.isInteger(index) && index >= 0 && index < current.length
  )));
  if (indexSet.size === 0) {
    return current.map((candidate, candidateIndex) => relabelAssemblyMateSelectionCandidate(candidate, candidateIndex));
  }

  return current
    .filter((_, candidateIndex) => !indexSet.has(candidateIndex))
    .map((candidate, candidateIndex) => relabelAssemblyMateSelectionCandidate(candidate, candidateIndex));
}

/**
 * 读取当前前两个配合候选。
 *
 * @param candidates 候选集合。
 * @returns 两个候选；不足时返回 `null`。
 */
export function resolveMateCandidatePair(
  candidates: readonly AssemblyMateSelectionCandidate[],
): readonly [AssemblyMateSelectionCandidate, AssemblyMateSelectionCandidate] | null {
  const first = candidates[0];
  const second = candidates[1];
  if (!first || !second) {
    return null;
  }
  return [first, second] as const;
}
